import type { ReactNode, Ref } from "react";

// The units one or more playbooks acted on (docs/design-system.md §0.4a, §0.6,
// §0.8; plan M8): one row per attribution unit, fixed columns from the
// playbook and the ledger, cells that only show. Wide tables scroll in their
// own box, with the unit column kept in view. A campaign's table (plan M8
// step 8d) groups rows by playbook in one table, so columns line up across
// groups, and adds a selection column in front, kept in view with the unit.

export interface UnitColumn { key: string; label: string; align?: "end"; mono?: boolean }
export interface UnitTableRow { id: string; cells: Record<string, ReactNode> }
export interface UnitTableGroup { id: string; header: ReactNode; rows: readonly UnitTableRow[] }
export interface UnitSelection {
  label: string;
  selected: ReadonlySet<string>;
  /** Rows that cannot be selected show no checkbox. */
  selectable: (id: string) => boolean;
  onToggle: (id: string) => void;
  /** The keyboard's row. */
  cursor?: string | null;
  checkboxLabel: (id: string) => string;
  rowRef?: (id: string) => Ref<HTMLTableRowElement>;
}

export function UnitTable({ label, columns, rows, groups, selection }: {
  label: string; columns: readonly UnitColumn[]; rows?: readonly UnitTableRow[]; groups?: readonly UnitTableGroup[]; selection?: UnitSelection;
}) {
  const span = columns.length + (selection ? 1 : 0);
  const body = (list: readonly UnitTableRow[]) => list.map((row) => {
    const selected = selection?.selected.has(row.id) ?? false;
    return <tr key={row.id} ref={selection?.rowRef?.(row.id)} data-selected={selected ? "" : undefined} data-cursor={selection?.cursor === row.id ? "" : undefined}>
      {selection && <td className="z-unit-select">{selection.selectable(row.id)
        && <input type="checkbox" checked={selected} onChange={() => selection.onToggle(row.id)} aria-label={selection.checkboxLabel(row.id)} />}</td>}
      {columns.map((column, index) => {
        const cell = row.cells[column.key];
        const content = cell === undefined || cell === null || cell === "" ? <span className="z-muted">–</span> : cell;
        return index === 0
          ? <th key={column.key} scope="row" className={column.mono ? "z-mono" : undefined}>{content}</th>
          : <td key={column.key} data-align={column.align} className={column.mono ? "z-mono" : undefined}>{content}</td>;
      })}
    </tr>;
  });
  return <div className="z-unit-scroll" role="region" aria-label={label} tabIndex={0} data-selectable={selection ? "" : undefined}>
    <table className="z-unit-table">
      <thead><tr>
        {selection && <th scope="col" className="z-unit-select" aria-label={selection.label} />}
        {columns.map((column) => <th key={column.key} scope="col" data-align={column.align}>{column.label}</th>)}
      </tr></thead>
      {groups
        ? groups.map((group) => <tbody key={group.id}>
          <tr className="z-unit-grouprow"><th scope="rowgroup" colSpan={span}><div className="z-unit-grouphead">{group.header}</div></th></tr>
          {body(group.rows)}
        </tbody>)
        : <tbody>{body(rows ?? [])}</tbody>}
    </table>
  </div>;
}
